import React, { useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Link } from 'react-router-dom'
import { useFamilyPortalApiClient } from '../features/auth/hooks/useFamilyPortalApiClient'
import { useHomeGreetingName } from '../features/home/hooks/useHomeGreetingName'
import ProgressCard, { type ProgressCardStep } from '../features/onboarding/components/ProgressCard'
import {
  buildConsentFlowSteps,
  isPractitionerMediatedConsentFlow,
  isPrimaryConsentFlow,
  resolveUserConsentFlow,
  type UserConsentFlowContext,
} from '../utils/consentFlowHelper'

function getOnboardingSteps(consentFlow: string | null | undefined): ProgressCardStep[] {
  const consentFlowSteps = buildConsentFlowSteps(consentFlow)
  return isPractitionerMediatedConsentFlow(consentFlow)
    ? [consentFlowSteps.secondary, consentFlowSteps.profile]
    : [consentFlowSteps.consent, consentFlowSteps.secondary, consentFlowSteps.profile]
}

function IntroProgressLoadingCard() {
  return (
    <section className="border-border-base rounded-xl border bg-white px-5 py-6 shadow-sm sm:px-8">
      <h2 className="text-2xl leading-9 font-semibold tracking-tight text-[#002C22]">
        <FormattedMessage id="intro.progressLoading.heading" defaultMessage="Up Next" />
      </h2>
      <p className="text-text-body mt-3 text-sm leading-6" aria-live="polite">
        <FormattedMessage id="intro.progressLoading.body" defaultMessage="Loading your next steps..." />
      </p>
    </section>
  )
}

const Intro: React.FC = () => {
  const apiClient = useFamilyPortalApiClient()
  const firstName = useHomeGreetingName(true) ?? undefined
  const [consentFlow, setConsentFlow] = useState<string | null>(null)
  const [contextLoaded, setContextLoaded] = useState(false)
  const onboardingSteps = useMemo(() => getOnboardingSteps(consentFlow), [consentFlow])

  useEffect(() => {
    let cancelled = false

    apiClient
      .get('/consents/context', undefined, true)
      .then((res) => {
        if (cancelled) return undefined
        if (!res.ok) return undefined
        return res.json()
      })
      .then((data: UserConsentFlowContext | undefined) => {
        if (cancelled) return
        setConsentFlow(resolveUserConsentFlow(data)?.trim() || null)
        setContextLoaded(true)
      })
      .catch(() => {
        if (cancelled) return
        setContextLoaded(true)
      })

    return () => {
      cancelled = true
    }
  }, [apiClient])

  return (
    <div className="flex w-full justify-center font-sans">
      <div className="flex w-full max-w-[900px] flex-col gap-10 pb-10">
        <section className="border-border-base rounded-xl border bg-white px-5 py-8 shadow-lg shadow-gray-900/10 sm:px-8 sm:py-12">
          <div className="max-w-3xl">
            <h1 className="text-3xl font-semibold tracking-tight text-[#002C22] sm:text-4xl">
              <FormattedMessage
                id="intro.heading.greeting"
                defaultMessage="Thanks for logging in{firstName, select, undefined {} other {, {firstName}}}."
                values={{ firstName }}
              />
            </h1>
            {contextLoaded &&
              (isPrimaryConsentFlow(consentFlow) ? (
                <>
                  <p className="text-text-body mt-8 text-base leading-7">
                    <FormattedMessage
                      id="intro.body.modeB.firstParagraph"
                      defaultMessage="Since this is your first time here, we'll walk you through a few questions."
                    />
                  </p>
                  <p className="text-text-body mt-8 text-base leading-7">
                    <FormattedMessage
                      id="intro.body.modeB.secondParagraph"
                      defaultMessage="You'll be asked to provide consent for the genetic test your doctor ordered, along with your preferences for how your family's information and samples are used."
                    />
                  </p>
                </>
              ) : (
                <p className="text-text-body mt-8 text-base leading-7">
                  <FormattedMessage
                    id="intro.body.modeA.firstParagraph"
                    defaultMessage="Since this is your first time here, we'll walk you through a few questions about how you would like your family's information and samples to be used."
                  />
                </p>
              ))}
            {contextLoaded && (
              <p className="text-text-body mt-8 text-base leading-7">
                <FormattedMessage
                  id="intro.body.closing"
                  defaultMessage="We'll make it simple, and the whole process should only take about five minutes."
                />
              </p>
            )}
            <Link
              to="/consents"
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-[#006045] px-5 py-3 text-sm font-semibold text-white shadow-xs transition hover:bg-[#004d37] focus:ring-2 focus:ring-[#006045]/20 focus:outline-none"
            >
              <span>
                <FormattedMessage id="intro.cta.getStarted" defaultMessage="Let's get started" />
              </span>
              <img src="/icons/arrow-right.svg" alt="" className="h-4 w-4" aria-hidden />
            </Link>
          </div>
        </section>

        {contextLoaded ? (
          <ProgressCard headingKey="intro.progressCard.heading" steps={onboardingSteps} />
        ) : (
          <IntroProgressLoadingCard />
        )}
      </div>
    </div>
  )
}

export default Intro
